import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useCurrentCrewMember } from '@/hooks/useCurrentCrewMember'
import { useDocumentGetByCrew } from '@/lib/hooks/document'
import {
  FileText,
  FileBadge2,
  IdCard,
  SquareUserRound,
  File,
  IdCardLanyard,
  Plus,
} from '@/components/icons'
import { DocumentSubType, documentSubTypeLabels } from '@@api/shared'
import { DocumentListCard } from '@/components/DocumentListCard'
import { ViewDocumentDrawer } from '@/components/documents/ViewDocumentDrawer'
import { DocumentFormDrawer } from '@/components/documents/DocumentFormDrawer'
import { TopBarBackButton } from '@/components/topbar'
import { Button } from '@/components/ui/button'
import { AppLayout } from '@/components/AppLayout'
import { DesktopPage } from '@/components/desktop/DesktopPage'
import { DesktopPageHeader } from '@/components/desktop/DesktopPageHeader'
import { useIsDesktop } from '@/components/ui/use-mobile'
import { ListSkeleton } from '@/components/ui/skeleton-presets'
import { EmptyState } from '@/components/EmptyState'
import dayjs from 'dayjs'

export const Route = createFileRoute('/_auth/profile/documents')({
  component: MyDocumentsRoute,
})

function getDocumentIcon(type: DocumentSubType) {
  switch (type) {
    case DocumentSubType.CrewID:
      return IdCardLanyard;
    case DocumentSubType.Passport:
      return IdCard
    case DocumentSubType.Visa:
      return FileBadge2
    case DocumentSubType.CrewID:
      return SquareUserRound
    default:
      return FileText
  }
}

function getDocumentExpiryStatus(
  expiryDate: Date | string
): 'expired' | 'expiring' | 'valid' {
  const now = dayjs()
  const expiry = dayjs(expiryDate)
  if (expiry.isBefore(now)) return 'expired'
  if (expiry.isBefore(now.add(2, 'month'))) return 'expiring'
  return 'valid'
}

function MyDocumentsRoute() {
  const isDesktop = useIsDesktop()
  const { data: currentUser } = useCurrentCrewMember()
  const { data: documents, isLoading } = useDocumentGetByCrew(
    currentUser?.id ?? ''
  )
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null
  )
  // Separate open flag; the id is kept on close so the view drawer can play
  // its exit animation (the next tap overwrites it).
  const [isViewDrawerOpen, setIsViewDrawerOpen] = useState(false)
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false)

  const addButton = (
    <Button
      variant="secondary"
      size="sm"
      roundness="round"
      onClick={() => setIsAddDrawerOpen(true)}
      className="size-8 p-0"
    >
      <Plus className="size-4" />
    </Button>
  )

  const body = (
    <>
      {isLoading ? (
        <ListSkeleton />
      ) : documents && documents.length > 0 ? (
        <div className="flex flex-col gap-3">
          {documents.map((doc) => {
            const DocumentIcon = getDocumentIcon(doc.subType)
            const expiryStatus = doc.expiryDate ? getDocumentExpiryStatus(doc.expiryDate) : undefined
            return (
              <DocumentListCard
                key={doc.id}
                icon={<DocumentIcon />}
                title={doc.title}
                subTypeLabel={
                  documentSubTypeLabels[doc.subType] || doc.subType
                }
                expiryLabel={doc.expiryDate ? `Expires ${new Date(doc.expiryDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : undefined}
                expiryStatus={expiryStatus}
                onClick={() => {
                  setSelectedDocumentId(doc.id)
                  setIsViewDrawerOpen(true)
                }}
              />
            )
          })}
        </div>
      ) : (
        <EmptyState
          icon={File}
          title="No documents"
          description="You don't have any documents yet"
        />
      )}

      <ViewDocumentDrawer
        isOpen={isViewDrawerOpen}
        onClose={() => setIsViewDrawerOpen(false)}
        documentId={selectedDocumentId}
        canManage
      />

      <DocumentFormDrawer
        isOpen={isAddDrawerOpen}
        onClose={() => setIsAddDrawerOpen(false)}
        owner={{ type: 'crew', crewId: currentUser?.id ?? '' }}
      />
    </>
  )

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title="My Documents"
          breadcrumb={
            <span>
              <Link to="/profile" className="hover:underline">
                Profile
              </Link>
              <span className="text-content-primary-subtle"> / Documents</span>
            </span>
          }
          action={addButton}
        />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout
      topBar={<TopBarBackButton title="My Documents" rightElement={addButton} />}
      className="py-6"
    >
      {body}
    </AppLayout>
  )
}
